const STORAGE_KEY = 'zyora-wishlist';

/** @returns {Array<{id: string, title: string, url: string, image: string, price: string}>} */
export function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch (error) {
    return [];
  }
}

function saveWishlist(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
}

export function isInWishlist(id) {
  return getWishlist().some((item) => String(item.id) === String(id));
}

function trackWishlistEvent(eventName, pixelEventName, product) {
  try {
    const payload = {
      ProductName: product.title,
      ProductID: product.id,
      URL: product.url,
      ImageURL: product.image,
      Price: product.price,
    };
    if (window.klaviyo && typeof window.klaviyo.track === 'function') {
      window.klaviyo.track(eventName, payload);
    } else if (window.klaviyo && typeof window.klaviyo.push === 'function') {
      window.klaviyo.push(['track', eventName, payload]);
    } else if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(['track', eventName, payload]);
    }
  } catch (error) {
    // Klaviyo not available, ignore.
  }

  try {
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(pixelEventName, {
        productId: product.id,
        productTitle: product.title,
        productPrice: product.price,
        productUrl: product.url,
      });
    }
  } catch (error) {
    // Shopify analytics not available, ignore.
  }
}

export function addToWishlist(product) {
  const items = getWishlist();
  if (items.some((item) => String(item.id) === String(product.id))) return;
  items.push(product);
  saveWishlist(items);
  trackWishlistEvent('Added to Wishlist', 'wishlist_add', product);
}

export function removeFromWishlist(id, product) {
  const items = getWishlist();
  const removed = items.find((item) => String(item.id) === String(id));
  saveWishlist(items.filter((item) => String(item.id) !== String(id)));
  trackWishlistEvent('Removed from Wishlist', 'wishlist_remove', product || removed);
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.handleClick = this.handleClick.bind(this);
    this.updateState = this.updateState.bind(this);
    this.updateState();
    this.button.addEventListener('click', this.handleClick);
    window.addEventListener('wishlist:updated', this.updateState);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    window.removeEventListener('wishlist:updated', this.updateState);
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const product = {
      id: this.dataset.productId,
      title: this.dataset.productTitle,
      url: this.dataset.productUrl,
      image: this.dataset.productImage,
      price: this.dataset.productPrice,
    };

    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id, product);
    } else {
      addToWishlist(product);
    }

    this.updateState();
  }

  updateState() {
    const active = isInWishlist(this.dataset.productId);
    this.classList.toggle('wishlist-button--active', active);
    this.button?.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
